/**
 * DCMCloud — Structured Reporting Panel
 * Supports: BI-RADS (breast), LI-RADS (liver), PI-RADS (prostate), Lung-RADS
 * Outputs plain-text report that can be copied or sent to the reporting API.
 */
import React, { useState, useCallback } from 'react';

// ─── Template definitions ────────────────────────────────────────────────────

const TEMPLATES = {
  'BI-RADS': {
    label: 'BI-RADS (Breast)',
    color: '#ec4899',
    categories: [
      { value: '0', label: '0 — Incomplete / additional imaging needed' },
      { value: '1', label: '1 — Negative (no abnormality)' },
      { value: '2', label: '2 — Benign finding' },
      { value: '3', label: '3 — Probably benign (<2% malignancy)' },
      { value: '4A', label: '4A — Low suspicion (>2%, ≤10%)' },
      { value: '4B', label: '4B — Moderate suspicion (>10%, ≤50%)' },
      { value: '4C', label: '4C — High suspicion (>50%, <95%)' },
      { value: '5', label: '5 — Highly suggestive of malignancy (≥95%)' },
      { value: '6', label: '6 — Known biopsy-proven malignancy' },
    ],
    fields: [
      { id: 'technique', label: 'Technique', type: 'text', placeholder: 'e.g. Digital mammography bilateral MLO/CC' },
      { id: 'composition', label: 'Breast Composition', type: 'select', options: ['a – Almost entirely fatty', 'b – Scattered fibroglandular', 'c – Heterogeneously dense', 'd – Extremely dense'] },
      { id: 'findings', label: 'Findings', type: 'textarea', placeholder: 'Describe masses, calcifications, architectural distortion, asymmetries…' },
      { id: 'comparison', label: 'Comparison', type: 'text', placeholder: 'Prior mammogram date / none' },
      { id: 'impression', label: 'Impression', type: 'textarea', placeholder: 'Summarise key findings and recommendation…' },
    ],
  },

  'LI-RADS': {
    label: 'LI-RADS (Liver)',
    color: '#f59e0b',
    categories: [
      { value: 'LR-1', label: 'LR-1 — Definitely benign' },
      { value: 'LR-2', label: 'LR-2 — Probably benign' },
      { value: 'LR-3', label: 'LR-3 — Intermediate probability of HCC' },
      { value: 'LR-4', label: 'LR-4 — Probably HCC' },
      { value: 'LR-5', label: 'LR-5 — Definitely HCC' },
      { value: 'LR-M', label: 'LR-M — Probably or definitely malignant, not HCC specific' },
      { value: 'LR-TIV', label: 'LR-TIV — Tumor in vein' },
    ],
    fields: [
      { id: 'technique', label: 'Technique', type: 'text', placeholder: 'e.g. Dynamic contrast-enhanced MRI liver 3T' },
      { id: 'clinicalContext', label: 'Clinical Context', type: 'text', placeholder: 'Cirrhosis / HBV / surveillance…' },
      { id: 'liverBackground', label: 'Liver Background', type: 'textarea', placeholder: 'Liver size, morphology, signal characteristics…' },
      { id: 'observation', label: 'Observation Description', type: 'textarea', placeholder: 'Segment, size, arterial enhancement, washout, capsule, threshold growth…' },
      { id: 'ancillary', label: 'Ancillary Features', type: 'textarea', placeholder: 'Fat, blood products, restricted diffusion, other…' },
      { id: 'impression', label: 'Impression', type: 'textarea', placeholder: 'Summarise LI-RADS category and recommendation…' },
    ],
  },

  'PI-RADS': {
    label: 'PI-RADS (Prostate)',
    color: '#3b82f6',
    categories: [
      { value: '1', label: '1 — Very low (clinically significant cancer highly unlikely)' },
      { value: '2', label: '2 — Low (clinically significant cancer unlikely)' },
      { value: '3', label: '3 — Intermediate (presence of clinically significant cancer uncertain)' },
      { value: '4', label: '4 — High (clinically significant cancer likely)' },
      { value: '5', label: '5 — Very high (clinically significant cancer highly likely)' },
    ],
    fields: [
      { id: 'technique', label: 'Technique', type: 'text', placeholder: 'e.g. mpMRI prostate 3T without endorectal coil' },
      { id: 'psa', label: 'PSA (ng/mL)', type: 'text', placeholder: 'e.g. 6.2' },
      { id: 'prostateVolume', label: 'Prostate Volume (cc)', type: 'text', placeholder: 'e.g. 42' },
      { id: 'zoneFindings', label: 'T2W Findings', type: 'textarea', placeholder: 'Peripheral zone, transition zone morphology…' },
      { id: 'dwiFindings', label: 'DWI / ADC Findings', type: 'textarea', placeholder: 'Restricted diffusion, ADC value…' },
      { id: 'dceFindings', label: 'DCE Findings', type: 'text', placeholder: 'Positive / Negative / Not performed' },
      { id: 'lesionLocation', label: 'Lesion Location', type: 'text', placeholder: 'e.g. Right PZ mid-gland, 12 o\'clock' },
      { id: 'impression', label: 'Impression', type: 'textarea', placeholder: 'Summarise PI-RADS score and recommendation…' },
    ],
  },

  'Lung-RADS': {
    label: 'Lung-RADS (Lung)',
    color: '#10b981',
    categories: [
      { value: '0', label: '0 — Incomplete' },
      { value: '1', label: '1 — Negative (no nodules / definitely benign)' },
      { value: '2', label: '2 — Benign appearance / behaviour' },
      { value: '3', label: '3 — Probably benign (<1% malignancy), 6-month CT' },
      { value: '4A', label: '4A — Suspicious, 3-month CT / PET-CT' },
      { value: '4B', label: '4B — Very suspicious, tissue sampling' },
      { value: '4X', label: '4X — Category 3 or 4 + additional features' },
      { value: 'S', label: 'S modifier — Clinically significant finding unrelated to lung cancer' },
    ],
    fields: [
      { id: 'technique', label: 'Technique', type: 'text', placeholder: 'e.g. Low-dose chest CT without contrast' },
      { id: 'ctdiVol', label: 'CTDIvol (mGy)', type: 'text', placeholder: 'e.g. 1.4' },
      { id: 'priorExam', label: 'Comparison', type: 'text', placeholder: 'Prior CT date / baseline' },
      { id: 'noduleDescription', label: 'Dominant Nodule', type: 'textarea', placeholder: 'Location, lobe, size (avg diameter mm), attenuation, margins…' },
      { id: 'additionalFindings', label: 'Additional Findings', type: 'textarea', placeholder: 'Other nodules, incidental findings, pleura, mediastinum…' },
      { id: 'impression', label: 'Impression', type: 'textarea', placeholder: 'Lung-RADS category and follow-up recommendation…' },
    ],
  },
};

// ─── Component ───────────────────────────────────────────────────────────────

export function RADSReportPanel() {
  const [activeTemplate, setActiveTemplate] = useState<keyof typeof TEMPLATES>('BI-RADS');
  const [category, setCategory] = useState('');
  const [fieldValues, setFieldValues] = useState<Record<string, string>>({});
  const [copied, setCopied] = useState(false);

  const tpl = TEMPLATES[activeTemplate];

  const handleTemplateChange = useCallback((key: keyof typeof TEMPLATES) => {
    setActiveTemplate(key);
    setCategory('');
    setFieldValues({});
    setCopied(false);
  }, []);

  const handleField = useCallback((id: string, value: string) => {
    setFieldValues(prev => ({ ...prev, [id]: value }));
  }, []);

  const buildReport = useCallback(() => {
    const lines: string[] = [
      `=== ${tpl.label} STRUCTURED REPORT ===`,
      `Category: ${category || '(not selected)'}`,
      '',
    ];
    for (const f of tpl.fields) {
      const val = fieldValues[f.id] || '';
      if (val) lines.push(`${f.label}:\n${val}\n`);
    }
    lines.push(`Generated by DCMCloud — ${new Date().toLocaleString()}`);
    return lines.join('\n');
  }, [tpl, category, fieldValues]);

  const copyReport = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(buildReport());
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Fallback for non-https contexts
      const ta = document.createElement('textarea');
      ta.value = buildReport();
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  }, [buildReport]);

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        background: 'var(--primary-bg, #1e1e2e)',
        color: 'var(--text-primary, #e2e8f0)',
        fontFamily: 'sans-serif',
        fontSize: '13px',
        overflowY: 'auto',
      }}
    >
      {/* Template selector */}
      <div style={{ padding: '10px 12px 4px', borderBottom: '1px solid #333' }}>
        <div style={{ fontSize: '11px', color: '#9ca3af', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
          RADS Template
        </div>
        <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
          {Object.entries(TEMPLATES).map(([key, t]) => (
            <button
              key={key}
              onClick={() => handleTemplateChange(key as keyof typeof TEMPLATES)}
              style={{
                padding: '4px 10px',
                borderRadius: '12px',
                border: `1px solid ${activeTemplate === key ? t.color : '#4b5563'}`,
                background: activeTemplate === key ? t.color + '22' : 'transparent',
                color: activeTemplate === key ? t.color : '#9ca3af',
                cursor: 'pointer',
                fontSize: '12px',
                fontWeight: activeTemplate === key ? '600' : '400',
              }}
            >
              {key}
            </button>
          ))}
        </div>
      </div>

      {/* Category / Score */}
      <div style={{ padding: '10px 12px', borderBottom: '1px solid #333' }}>
        <label style={{ display: 'block', color: '#9ca3af', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase' }}>
          {tpl.label} — Final Category
        </label>
        <select
          value={category}
          onChange={e => setCategory(e.target.value)}
          style={{
            width: '100%',
            background: '#111827',
            color: '#e2e8f0',
            border: `1px solid ${tpl.color}`,
            borderRadius: '6px',
            padding: '6px 8px',
            fontSize: '13px',
          }}
        >
          <option value="">— Select category —</option>
          {tpl.categories.map(c => (
            <option key={c.value} value={c.value}>{c.label}</option>
          ))}
        </select>
      </div>

      {/* Fields */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '10px 12px' }}>
        {tpl.fields.map(f => (
          <div key={f.id} style={{ marginBottom: '12px' }}>
            <label style={{ display: 'block', color: '#9ca3af', fontSize: '11px', marginBottom: '4px', textTransform: 'uppercase' }}>
              {f.label}
            </label>
            {f.type === 'textarea' ? (
              <textarea
                rows={3}
                placeholder={f.placeholder}
                value={fieldValues[f.id] || ''}
                onChange={e => handleField(f.id, e.target.value)}
                style={{
                  width: '100%',
                  background: '#111827',
                  color: '#e2e8f0',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  padding: '6px 8px',
                  fontSize: '12px',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
            ) : f.type === 'select' ? (
              <select
                value={fieldValues[f.id] || ''}
                onChange={e => handleField(f.id, e.target.value)}
                style={{
                  width: '100%',
                  background: '#111827',
                  color: '#e2e8f0',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  padding: '6px 8px',
                  fontSize: '12px',
                }}
              >
                <option value="">— Select —</option>
                {(f.options || []).map(o => (
                  <option key={o} value={o}>{o}</option>
                ))}
              </select>
            ) : (
              <input
                type="text"
                placeholder={f.placeholder}
                value={fieldValues[f.id] || ''}
                onChange={e => handleField(f.id, e.target.value)}
                style={{
                  width: '100%',
                  background: '#111827',
                  color: '#e2e8f0',
                  border: '1px solid #374151',
                  borderRadius: '6px',
                  padding: '6px 8px',
                  fontSize: '12px',
                  boxSizing: 'border-box',
                }}
              />
            )}
          </div>
        ))}
      </div>

      {/* Footer actions */}
      <div style={{ padding: '10px 12px', borderTop: '1px solid #333', display: 'flex', gap: '8px' }}>
        <button
          onClick={copyReport}
          style={{
            flex: 1,
            padding: '8px',
            borderRadius: '6px',
            background: copied ? '#059669' : tpl.color,
            color: '#fff',
            border: 'none',
            cursor: 'pointer',
            fontWeight: '600',
            fontSize: '13px',
          }}
        >
          {copied ? '✓ Copied!' : 'Copy Report'}
        </button>
        <button
          onClick={() => { setCategory(''); setFieldValues({}); }}
          style={{
            padding: '8px 14px',
            borderRadius: '6px',
            background: 'transparent',
            color: '#9ca3af',
            border: '1px solid #374151',
            cursor: 'pointer',
            fontSize: '13px',
          }}
        >
          Clear
        </button>
      </div>
    </div>
  );
}

export default RADSReportPanel;
